import { nip19 } from 'nostr-tools';
import { CopyIcon, CheckIcon, ExternalLinkIcon } from 'lucide-react';
import { useState } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import type { NostrMetadata } from '@nostrify/nostrify';

interface ProfileHeaderProps {
  pubkey: string;
  metadata: NostrMetadata | undefined;
  isLoading: boolean;
}

export function ProfileHeader({ pubkey, metadata, isLoading }: ProfileHeaderProps) {
  const [copied, setCopied] = useState(false);
  const npub = nip19.npubEncode(pubkey);
  const displayName = metadata?.name ?? metadata?.display_name ?? npub.slice(0, 12) + '...';
  const initial = displayName.charAt(0).toUpperCase();

  const copyNpub = () => {
    navigator.clipboard.writeText(npub);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center text-center">
        {/* Banner skeleton */}
        <Skeleton className="w-full h-32 rounded-xl mb-[-2rem]" />
        <Skeleton className="size-20 rounded-full relative z-10 ring-4 ring-background" />
        <Skeleton className="h-6 w-40 mt-3" />
        <Skeleton className="h-4 w-64 mt-2" />
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center text-center">
      {/* Banner */}
      {metadata?.banner ? (
        <div className="w-full h-32 sm:h-40 rounded-xl overflow-hidden mb-[-2.5rem]">
          <img
            src={metadata.banner}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
      ) : (
        <div className="w-full h-32 sm:h-40 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 mb-[-2.5rem]" />
      )}

      {/* Avatar */}
      <Avatar className="size-20 relative z-10 ring-4 ring-background">
        <AvatarImage src={metadata?.picture} alt={displayName} />
        <AvatarFallback className="text-xl font-bold">{initial}</AvatarFallback>
      </Avatar>

      {/* Name & about */}
      <h1 className="text-xl font-bold mt-3">{displayName}</h1>

      {metadata?.nip05 && (
        <p className="text-sm text-primary font-medium">{metadata.nip05}</p>
      )}

      {metadata?.about && (
        <p className="text-sm text-muted-foreground mt-2 max-w-md leading-relaxed">
          {metadata.about}
        </p>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 mt-4">
        <Button
          variant="outline"
          size="sm"
          className="gap-1.5 text-xs"
          onClick={copyNpub}
        >
          {copied ? (
            <CheckIcon className="size-3.5" />
          ) : (
            <CopyIcon className="size-3.5" />
          )}
          {copied ? 'Copied!' : npub.slice(0, 8) + '...' + npub.slice(-4)}
        </Button>

        {metadata?.website && (
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5 text-xs"
            asChild
          >
            <a href={metadata.website} target="_blank" rel="noopener noreferrer">
              <ExternalLinkIcon className="size-3.5" />
              Website
            </a>
          </Button>
        )}
      </div>
    </div>
  );
}
